import { useState } from 'react'
import { toast } from 'sonner'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { Button, Card } from '@/components/ui'
import { useConnectors } from '@/hooks/useConnectors'
import { usePackageFeeds, useCreatePackageFeed, useUpdatePackageFeed } from '@/hooks/usePackageFeeds'
import { getErrorCode, getErrorMessage } from '@/api/errors'
import { PackageFeedDialog } from './PackageFeedDialog'
import { DeletePackageFeedDialog } from './DeletePackageFeedDialog'
import type { PackageFeed, SaveFeedRequest } from '@/types'

export interface PackageFeedsSectionProps {
  projectId: string
  canManage: boolean
}

export function PackageFeedsSection({ projectId, canManage }: PackageFeedsSectionProps) {
  const { data: feeds = [], isLoading } = usePackageFeeds(projectId)
  const { data: connectors = [] } = useConnectors(projectId)
  const availableConnectorTypes = connectors.filter(c => c.category !== 'CodeQuality' && c.category !== 'AiProvider').map(c => c.type)

  const createFeed = useCreatePackageFeed(projectId)
  const updateFeed = useUpdatePackageFeed(projectId)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<PackageFeed | undefined>(undefined)
  const [inlineError, setInlineError] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PackageFeed | undefined>(undefined)

  const openCreate = () => { setEditing(undefined); setInlineError(null); setDialogOpen(true) }
  const openEdit = (feed: PackageFeed) => { setEditing(feed); setInlineError(null); setDialogOpen(true) }

  const handleSave = async (feed: SaveFeedRequest) => {
    setInlineError(null)
    try {
      if (editing) {
        await updateFeed.mutateAsync({ feedId: editing.id, feed })
        toast.success('Package feed updated.')
      } else {
        await createFeed.mutateAsync(feed)
        toast.success('Package feed added.')
      }
    } catch (err) {
      if (getErrorCode(err) === 'CONNECTOR_UNAUTHORIZED')
        setInlineError("This token can't read the feed — check its read/Packaging scope.")
      else
        setInlineError(getErrorMessage(err) ?? 'Failed to save feed.')
      throw err
    }
  }


  return (
    <Card className="p-6 flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-semibold">Package feeds</h2>
          <p className="text-sm text-muted-foreground">
            Private NuGet feeds available to this project's workflow sandboxes. Tokens are write-only and never displayed.
          </p>
        </div>
        {canManage && <Button onClick={openCreate} data-testid="feed-add"><Plus className="h-4 w-4 mr-1.5" /> Add feed</Button>}
      </div>

      {isLoading ? (
        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">Loading…</p>
      ) : feeds.length === 0 ? (
        <p data-testid="feeds-empty" className="text-sm text-muted-foreground">No package feeds configured.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {feeds.map(feed => (
            <li key={feed.id} data-testid="feed-row" className="flex items-center justify-between rounded-md border p-3">
              <div className="flex flex-col">
                <span className="font-medium">{feed.name} <span className="text-xs text-muted-foreground">({feed.feedType})</span></span>
                <span className="text-xs text-muted-foreground break-all">{feed.feedUrl}</span>
                <span className="text-xs text-muted-foreground">
                  user: {feed.username} · {feed.tokenSource === 'Connector' ? `uses ${feed.reuseConnectorType} connector` : 'token configured'}
                </span>
              </div>
              {canManage && (
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" aria-label="Edit feed" onClick={() => openEdit(feed)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" aria-label="Delete feed" onClick={() => setDeleteTarget(feed)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <PackageFeedDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        availableConnectorTypes={availableConnectorTypes}
        isSaving={createFeed.isPending || updateFeed.isPending}
        inlineError={inlineError}
        onSave={handleSave}
      />

      {deleteTarget && (
        <DeletePackageFeedDialog
          projectId={projectId}
          feed={deleteTarget}
          open={!!deleteTarget}
          onOpenChange={(open) => { if (!open) setDeleteTarget(undefined) }}
        />
      )}
    </Card>
  )
}
